/* istanbul ignore file */
import { useState } from 'react';
import { useScript } from 'src/hooks/useScript';
import { useSidebarOverlay } from 'src/hooks/useSidebarOverlay';
import { useFlags } from './feature-flags';

const APOLLO_SCRIPT_ID = 'apollo-widget-script';
const APOLLO_SCRIPT_SRC =
  'https://assets.apollo.io/js/meetings/meetings-widget.js';

let isInitialized = false;

export const useApolloMeetings = () => {
  const {
    'exp_04212025-meeting-dialog-override': LDFF_expMeetingDialogOverride,
  } = useFlags(['exp_04212025-meeting-dialog-override']);
  const [showOverlay, setShowOverlay] = useState(false);
  useSidebarOverlay(showOverlay, 'apollo-meetings');

  const scriptStatus = useScript({
    src: APOLLO_SCRIPT_SRC,
    id: APOLLO_SCRIPT_ID,
    defer: true,
    onLoad: () => {
      if (!isInitialized && window.ApolloMeetings) {
        window.ApolloMeetings.initWidget({
          appId: !LDFF_expMeetingDialogOverride
            ? '67f930b348cc130019696df9'
            : '67ec32b66a6de6001507c568',
          schedulingLink: !LDFF_expMeetingDialogOverride
            ? 'g1n-b18-xku'
            : '1nx-zdr-ezv',
        });
        isInitialized = true;
      }
    },
    onError: (error) => {
      console.error('Failed to load Apollo Meetings script:', error);
    },
  });

  const submitMeeting = (
    lead: {
      email: string;
      full_name: string;
      company_name: string;
      company_size?: number;
      company_location?: string;
    },
    onSuccess: () => void,
    {
      onClose,
      closeOnOutside = true,
    }: { onClose?: () => void; closeOnOutside?: boolean } = {},
  ) => {
    try {
      if (scriptStatus !== 'ready') {
        console.error('ApolloMeetings script is not ready');
        return;
      }

      if (!window.ApolloMeetings) {
        console.error('ApolloMeetings not found on window');
        return;
      }

      if (!isInitialized) {
        console.error('Apollo widget not initialized');
        return;
      }

      setShowOverlay(true);

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            const widgetContainer = document.querySelector(
              '#apollo-meetings-widget',
            ) as HTMLElement;

            if (
              widgetContainer &&
              getComputedStyle(widgetContainer).display === 'none'
            ) {
              setShowOverlay(false);
              onClose?.();
              observer.disconnect();
            }
          }
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      window.ApolloMeetings.submit({
        map: false,
        closeOnOutside: closeOnOutside,
        lead: {
          email: lead.email ?? '',
          full_name: lead.full_name ?? '',
          company_name: lead.company_name ?? '',
          company_size: lead.company_size ?? '',
          company_location: lead.company_location ?? '',
        },
        onSuccess: () => {
          onSuccess?.();
        },
        onError: (error: any) => {
          console.error('Error submitting meeting:', error);
          setShowOverlay(false);
          onClose?.();
          observer.disconnect();
        },
      });
    } catch (error) {
      console.error('Error submitting meeting:', error);
      setShowOverlay(false);
      onClose?.();
    }
  };

  return {
    submit: submitMeeting,
    isReady:
      scriptStatus === 'ready' && !!window.ApolloMeetings && isInitialized,
  };
};
